import {
  getProductCollection,
  getCategoriesCollection,
  isProductsCollection,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';

import {
  getRelativeBindingContextFields,
  defineCollectionTypeReference,
} from '@packages/systems/cms/gateway/shared';

import {
  PRODUCTS_BINDING_CONTEXT_ID,
  CATEGORIES_BINDING_CONTEXT_ID,
  MORE_IMAGES_BINDING_CONTEXT_ID,
  CART_BINDING_CONTEXT_ID,
  ADD_TO_CART_OPTION_CONTEXT_ID,
  fieldListBindingContextId,
} from './identifiers';

import {cartRelationships} from './cart';
import {orderItemRelationships} from './order-items';
import {checkoutPageRelationships} from './checkout-page';
import {paypalCheckoutPageRelationships} from './checkout-paypal-page';
import {orderConfirmationPageRelationships} from './order-confirmation-page';
import {orderRelationships} from './order';

import type {
  BindingContextRelationshipDef,
  TypeReferenceBuilder,
} from '@packages/systems/dynamo/source-types';
import type {
  Collections,
  CollectionRecordType as Collection,
} from '@packages/systems/dynamo/records/CollectionRecord';

export default function defineRelationships<RootTRef, EntityTRef, ListTRef>(
  collections: Collections,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextRelationshipDef<RootTRef, EntityTRef>[] {
  const products = getProductCollection(collections);
  const categories = getCategoriesCollection(collections);

  const rootRelationshipsDef = {
    type: referenceBuilder.root(),
    relativeContexts: [
      PRODUCTS_BINDING_CONTEXT_ID,
      CATEGORIES_BINDING_CONTEXT_ID,
      CART_BINDING_CONTEXT_ID,
    ],
  };

  const relationshipDefs = [
    rootRelationshipsDef,
    cartRelationships(referenceBuilder),
    orderItemRelationships(referenceBuilder),
    checkoutPageRelationships(referenceBuilder),
    paypalCheckoutPageRelationships(referenceBuilder),
    orderConfirmationPageRelationships(referenceBuilder),
    ...orderRelationships(referenceBuilder),
  ];

  if (products) {
    relationshipDefs.push(
      defineCollectionRelationships(products, referenceBuilder)
    );
  }

  if (categories) {
    relationshipDefs.push(
      defineCollectionRelationships(categories, referenceBuilder)
    );
  }

  return relationshipDefs;
}

function defineCollectionRelationships<RootTRef, EntityTRef, ListTRef>(
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextRelationshipDef<RootTRef, EntityTRef> {
  const relativeContexts = getRelativeBindingContextFields(collection).map(
    (field) => fieldListBindingContextId(field.id)
  );
  return {
    type: referenceBuilder.entityOf(defineCollectionTypeReference(collection)),
    relativeContexts: isProductsCollection(collection)
      ? [
          ...relativeContexts,
          MORE_IMAGES_BINDING_CONTEXT_ID,
          ADD_TO_CART_OPTION_CONTEXT_ID,
        ]
      : relativeContexts,
  };
}
